<template>
  <Layout>
    <SEOStructuredData 
      :data="structuredData"
      type="website"
    />
    <div class="tappay-payment-page">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-md-8">
            <!-- 進度條 -->
            <div class="checkout-progress">
              <div class="custom-progress-bar">
                <div class="progress-step">
                  <div class="step-number">1</div>
                  <div class="step-label">購物車</div>
                </div>
                <div class="progress-line"></div>
                <div class="progress-step">
                  <div class="step-number">2</div>
                  <div class="step-label">結帳</div>
                </div>
                <div class="progress-line"></div>
                <div class="progress-step active">
                  <div class="step-number">3</div>
                  <div class="step-label">付款</div>
                </div>
              </div>
            </div>

            <!-- 訂單摘要 -->
            <div class="order-summary">
              <h3>訂單摘要</h3>
              <div class="summary-item">
                <span>商品總計</span>
                <span>NT$ {{ subtotal.toLocaleString() }}</span>
              </div>
              <div class="summary-item">
                <span>運費</span>
                <span>NT$ {{ shipping.toLocaleString() }}</span>
              </div>
              <div class="summary-item total">
                <span>總計</span>
                <span>NT$ {{ total.toLocaleString() }}</span>
              </div>
            </div>

            <!-- 付款方式選擇 -->
            <div class="payment-method-selection" v-if="!selectedPaymentMethod">
              <h3>選擇付款方式</h3>
              <div class="payment-options">
                <!-- 信用卡付款 -->
                <div class="payment-option" @click="selectPaymentMethod('card')">
                  <div class="option-header">
                    <div class="payment-icons">
                      <img src="@/assets/img/visa.svg" alt="Visa">
                      <img src="@/assets/img/master-card.svg" alt="MasterCard">
                      <img src="@/assets/img/jcb.svg" alt="JCB">
                      <img src="@/assets/img/unionpay.svg" alt="UnionPay">
                    </div>
                    <span class="option-name">信用卡付款</span>
                  </div>
                  <span class="option-desc">支援 Visa、MasterCard、JCB、銀聯</span>
                </div>

                <!-- Apple Pay -->
                <div class="payment-option" @click="selectPaymentMethod('apple_pay')" v-if="applePayAvailable">
                  <div class="option-header">
                    <img src="@/assets/img/apple-pay.svg" alt="Apple Pay" class="option-icon">
                    <span class="option-name">Apple Pay</span>
                  </div>
                  <span class="option-desc">使用 Apple Pay 快速付款</span>
                </div>

                <!-- Line Pay -->
                <div class="payment-option" @click="selectPaymentMethod('line_pay')" v-if="linePayAvailable">
                  <div class="option-header">
                    <img src="@/assets/img/line-pay.svg" alt="LINE Pay" class="option-icon">
                    <span class="option-name">LINE Pay</span>
                  </div>
                  <span class="option-desc">使用 LINE Pay 快速付款</span>
                </div>
              </div>
            </div>

            <!-- 信用卡付款表單 -->
            <div class="payment-card" v-if="selectedPaymentMethod === 'card'">
              <div class="payment-form">
                <h3>信用卡付款</h3>
                
                <!-- 付款說明 -->
                <div class="payment-info">
                  <p>請在下方輸入您的信用卡資訊進行付款。</p>
                  <p>所有交易都經過 SSL 加密保護。</p>
                </div>
                
                <!-- 信用卡輸入表單 -->
                <div class="credit-card-form">
                  <div class="form-group">
                    <label for="card-container">信用卡資訊</label>
                    <div id="card-container" class="card-input-field" :class="{ loading: sdkLoading }"></div>
                    <small class="form-text text-muted">請輸入您的信用卡號碼、有效期限和安全碼</small>
                  </div>
                </div>
                
                <!-- 付款按鈕 -->
                <button 
                  @click="processPayment" 
                  :disabled="loading"
                  class="payment-btn"
                >
                  {{ loading ? '處理中...' : '確認付款' }}
                </button>
                
                <!-- 錯誤訊息 -->
                <div v-if="error" class="error-message">
                  {{ error }}
                </div>
                
                <!-- 支援的卡片類型 -->
                <div class="supported-cards">
                  <h4>支援的卡片類型</h4>
                  <div class="card-icons">
                    <img src="@/assets/img/visa.svg" alt="Visa">
                    <img src="@/assets/img/master-card.svg" alt="MasterCard">
                    <img src="@/assets/img/jcb.svg" alt="JCB">
                    <img src="@/assets/img/unionpay.svg" alt="UnionPay">
                  </div>
                </div>
                
                <!-- 安全說明 -->
                <div class="security-notice">
                  <h4>安全說明</h4>
                  <ul>
                    <li>您的卡片資訊將在 Tappay 的安全環境中處理</li>
                    <li>我們不會儲存您的卡片資訊</li>
                    <li>所有交易都經過 SSL 加密保護</li>
                    <li>支援 3D Secure 驗證</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Apple Pay 付款 -->
            <div class="payment-card" v-if="selectedPaymentMethod === 'apple_pay'">
              <div class="payment-form">
                <h3>Apple Pay 付款</h3>
                
                <div class="payment-info">
                  <p>點擊下方按鈕使用 Apple Pay 進行付款。</p>
                </div>
                
                <!-- Apple Pay 按鈕 -->
                <div class="apple-pay-container">
                  <div id="apple-pay-button" class="apple-pay-button"></div>
                </div>
                
                <!-- 錯誤訊息 -->
                <div v-if="error" class="error-message">
                  {{ error }}
                </div>
              </div>
            </div>

            <!-- Line Pay 付款 -->
            <div class="payment-card" v-if="selectedPaymentMethod === 'line_pay'">
              <div class="payment-form">
                <h3>LINE Pay 付款</h3>
                
                <div class="payment-info">
                  <p>點擊下方按鈕使用 LINE Pay 進行付款。</p>
                </div>
                
                <!-- Line Pay 按鈕 -->
                <div class="line-pay-container">
                  <div id="line-pay-button" class="line-pay-button"></div>
                </div>
                
                <!-- 錯誤訊息 -->
                <div v-if="error" class="error-message">
                  {{ error }}
                </div>
              </div>
            </div>

            <!-- 返回按鈕 -->
            <div class="back-button">
              <button @click="goBack" class="btn btn-secondary">
                返回結帳頁面
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Layout from '@/layouts/Layout.vue'
import SEOStructuredData from '@/components/SEOStructuredData.vue'
import http from '@/services/http'
import { TAPPAY_CONFIG } from '@/config/tappay'

export default {
  name: 'TappayPaymentPage',
  components: {
    Layout,
    SEOStructuredData
  },
  data() {
    return {
      orderInfo: {},
      loading: false,
      error: '',
      orderId: null,
      tappay: null,
      sdkLoading: true,
      selectedPaymentMethod: null,
      applePayAvailable: false,
      linePayAvailable: false,
      availablePaymentMethods: [],
      isGuestCheckout: false,
      orderDetails: null
    }
  },
  computed: {
    ...mapState(['cartItems']),
    subtotal() {
      if (this.isGuestCheckout && this.orderDetails) {
        // 访客结账：使用订单详情中的总金额
        return this.orderDetails.total_amount || 0
      } else {
        // 会员结账：使用购物车计算
        return this.cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
      }
    },
    shipping() {
      return 0
    },
    total() {
      return this.subtotal + this.shipping
    },
    structuredData() {
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "付款頁面",
        "description": "完成您的付款以確認您的訂單。",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "購物車",
              "item": "https://example.com/cart"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "結帳",
              "item": "https://example.com/checkout"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "付款",
              "item": "https://example.com/checkout/payment"
            }
          ]
        }
      }
    }
  },
  methods: {
    ...mapMutations(['setOrderInfo', 'setInvoiceInfo', 'setShippingMethod', 'setPaymentMethod']),
    
    async initTappaySDK() {
      // 檢查是否選擇了信用卡付款
      if (this.selectedPaymentMethod !== 'card') {
        console.log('Card payment not selected, skipping TapPay initialization')
        return
      }

      // 檢查 Tappay SDK 是否已載入
      if (typeof window.TPDirect === 'undefined') {
        console.warn('Tappay SDK not loaded, loading now...')
        // 動態載入 Tappay SDK
        this.loadTappaySDK()
      } else {
        console.log('Tappay SDK already loaded, initializing...')
        // SDK 已載入，直接初始化
        await this.initializeTappay()
      }
    },
    
    async loadAvailablePaymentMethods() {
      try {
        const response = await http.get('/api/v1/tappay/available-payment-methods')
        const data = await response.json()
        
        if (data.success) {
          this.availablePaymentMethods = data.payment_methods
          this.applePayAvailable = data.payment_methods.includes('apple_pay')
          this.linePayAvailable = data.payment_methods.includes('line_pay')
        }
      } catch (error) {
        console.error('Failed to load available payment methods:', error)
        // 預設啟用信用卡
        this.availablePaymentMethods = ['card']
      }
    },
    
    async loadGuestOrderDetails() {
      if (!this.isGuestCheckout || !this.orderId) return
      
      try {
        const response = await http.get(`/api/v1/orders/${this.orderId}`)
        const data = await response.json()
        
        if (data.order) {
          this.orderDetails = data.order
          console.log('访客订单详情:', this.orderDetails)
        }
      } catch (error) {
        console.error('Failed to load guest order details:', error)
        this.error = '無法載入訂單詳情'
      }
    },
    
    selectPaymentMethod(method) {
      this.selectedPaymentMethod = method
      this.error = ''
      
      // 根據付款方式初始化相應的SDK
      if (method === 'card') {
        // 等待 DOM 更新後再初始化 TapPay
        this.$nextTick(async () => {
          await this.initTappaySDK()
        })
      } else if (method === 'apple_pay') {
        this.initApplePay()
      } else if (method === 'line_pay') {
        this.initLinePay()
      }
    },
    
    async initApplePay() {
      if (typeof window.TPDirect === 'undefined') {
        this.error = 'Tappay SDK 尚未載入'
        return
      }
      
      try {
        // 初始化 Apple Pay
        window.TPDirect.applePay.setup({
          merchant_id: TAPPAY_CONFIG.APPLE_PAY_MERCHANT_ID,
          merchant_name: 'Your Store Name',
          supported_networks: ['visa', 'masterCard', 'amex'],
          merchant_capabilities: ['supports3DS'],
          country_code: 'TW',
          currency_code: 'TWD'
        })
        
        // 設置 Apple Pay 按鈕
        window.TPDirect.applePay.setupButton('#apple-pay-button', {
          type: 'buy',
          style: 'black'
        })
        
        // 監聽 Apple Pay 事件
        window.TPDirect.applePay.onPaymentAuthorized((result) => {
          this.handleApplePayPayment(result)
        })
        
      } catch (error) {
        console.error('Apple Pay setup error:', error)
        this.error = 'Apple Pay 初始化失敗'
      }
    },
    
    async initLinePay() {
      if (typeof window.TPDirect === 'undefined') {
        this.error = 'Tappay SDK 尚未載入'
        return
      }
      
      try {
        // 初始化 Line Pay
        window.TPDirect.linePay.setup({
          channel_id: TAPPAY_CONFIG.LINE_PAY_CHANNEL_ID
        })
        
        // 設置 Line Pay 按鈕
        window.TPDirect.linePay.setupButton('#line-pay-button', {
          type: 'pay',
          style: 'black'
        })
        
        // 監聽 Line Pay 事件
        window.TPDirect.linePay.onPaymentAuthorized((result) => {
          this.handleLinePayPayment(result)
        })
        
      } catch (error) {
        console.error('Line Pay setup error:', error)
        this.error = 'Line Pay 初始化失敗'
      }
    },
    
    async handleApplePayPayment(result) {
      if (result.status === 0) {
        await this.processPaymentWithPrime(result.prime, 'apple_pay')
      } else {
        this.error = `Apple Pay 付款失敗: ${result.msg}`
      }
    },
    
    async handleLinePayPayment(result) {
      if (result.status === 0) {
        await this.processPaymentWithPrime(result.prime, 'line_pay')
      } else {
        this.error = `Line Pay 付款失敗: ${result.msg}`
      }
    },
    
    testTappaySDK() {
      console.log('Testing Tappay SDK...')
      console.log('window.TPDirect:', window.TPDirect)
      console.log('window.TPDirect.setupSDK:', typeof window.TPDirect?.setupSDK)
      console.log('window.TPDirect.card:', window.TPDirect?.card)
      console.log('window.TPDirect.card.setup:', typeof window.TPDirect?.card?.setup)
    },
    
    loadTappaySDK() {
      // 動態載入 Tappay SDK
      const script = document.createElement('script')
      script.src = 'https://js.tappaysdk.com/tpdirect/v5.1.0'
      script.onload = () => {
        console.log('Tappay SDK loaded successfully')
        // 延遲一點時間確保 SDK 完全載入
        setTimeout(async () => {
          await this.initializeTappay()
        }, 500) // 增加延遲時間
      }
      script.onerror = () => {
        console.error('Failed to load Tappay SDK')
        this.error = '無法載入付款系統，請重新整理頁面'
        this.sdkLoading = false
      }
      document.head.appendChild(script)
    },
    
    async processPayment() {
      if (!this.tappay) {
        this.error = '付款系統尚未初始化，請重新整理頁面'
        return
      }

      this.loading = true
      this.error = ''
      
      try {
        // 如果是訪客結帳，跳過創建訂單（訂單已在前一步創建）
        if (!this.isGuestCheckout) {
          // 會員結帳：創建訂單
          const requestData = {
            tappay: {
              amount: this.total,
              shipping_method: this.orderInfo.shippingMethod,
              shipping_address: this.orderInfo.address
            }
          }
          
          console.log('Tappay request data:', requestData)
          
          const response = await http.post('/api/v1/tappay/create-order', requestData)
          
          const data = await response.json()
          console.log('Tappay response:', data)
          
          if (data.success && data.order_id) {
            this.orderId = data.order_id
          } else {
            throw new Error(data.error || '無法建立訂單')
          }
        }
        
        // 訪客和會員都執行：使用 Tappay 的 prime 進行付款
        if (this.orderId) {
          
          // 使用 Tappay 的 prime 進行付款
          if (typeof this.tappay.getPrime === 'function') {
            console.log('Calling Tappay getPrime...')
            console.log('Tappay instance:', this.tappay)
            
            try {
              this.tappay.getPrime((result) => {
                console.log('Tappay getPrime callback result:', result)
                // 取得 prime，優先 result.prime，否則取 result.card.prime
                const prime = result.prime || (result.card && result.card.prime)

                if (result.status !== 0) {
                  this.error = `無法取得付款資訊: ${result.msg || '請檢查卡片資料'}`
                  this.loading = false
                  return
                }

                if (!prime) {
                  this.error = 'Tappay 沒有返回 prime，請檢查卡片資料是否完整'
                  this.loading = false
                  return
                }

                // 提交付款，確保 prime 傳遞正確
                this.handlePaymentSuccess({ ...result, prime })
              })
            } catch (error) {
              console.error('Error calling getPrime:', error)
              this.error = '調用 Tappay 付款系統時發生錯誤(' + error + ')'
              this.loading = false
            }
          } else {
            throw new Error('Tappay getPrime method not available')
          }
        } else {
          throw new Error('訂單ID不存在，無法進行付款')
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.error = error.message || '付款處理失敗，請稍後再試'
        this.loading = false
      }
    },

    async processPaymentWithPrime(prime, paymentMethod) {
      this.loading = true
      this.error = ''
      
      try {
        // 如果是访客结账，跳过创建订单
        if (!this.isGuestCheckout) {
          // 會員結帳：創建訂單
          const requestData = {
            tappay: {
              amount: this.total,
              shipping_method: this.orderInfo.shippingMethod,
              shipping_address: this.orderInfo.address
            }
          }
          
          const response = await http.post('/api/v1/tappay/create-order', requestData)
          const data = await response.json()
          
          if (data.success && data.order_id) {
            this.orderId = data.order_id
          } else {
            throw new Error(data.error || '無法建立訂單')
          }
        }
        
        // 访客和会员都执行：使用 prime 进行付款
        if (this.orderId) {
          
          // 使用 prime 進行付款
          const paymentData = {
            tappay: {
              order_id: this.orderId,
              prime: prime,
              payment_method: paymentMethod
            }
          }
          
          const paymentResponse = await http.post('/api/v1/tappay/process-payment', paymentData)
          const paymentResult = await paymentResponse.json()
          
          if (paymentResult.success) {
            if (paymentResult.requires_3ds && paymentResult.payment_url) {
              // 需要 3D Secure 驗證，重定向到驗證頁面
              console.log('Redirecting to 3D Secure verification:', paymentResult.payment_url)
              window.location.href = paymentResult.payment_url
            } else {
              // 直接付款成功
              this.$router.push(`/checkout/done?order_id=${this.orderId}&payment_method=${paymentMethod}`)
            }
          } else {
            throw new Error(paymentResult.error || '付款處理失敗')
          }
        } else {
          throw new Error('訂單ID不存在，無法進行付款')
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.error = error.message || '付款處理失敗，請稍後再試'
        this.loading = false
      }
    },

    async initializeTappay() {
      // 檢查 Tappay SDK 是否已載入
      if (typeof window.TPDirect === 'undefined') {
        console.error('Tappay SDK not available')
        this.error = 'Tappay SDK 尚未載入，請重新整理頁面'
        this.sdkLoading = false
        return
      }

      // 測試 SDK 功能
      this.testTappaySDK()

      try {
        // 清理之前的實例
        if (this.tappay) {
          try {
            if (typeof this.tappay.destroy === 'function') {
              this.tappay.destroy()
            } else if (typeof this.tappay.remove === 'function') {
              this.tappay.remove()
            } else {
              console.log('Tappay instance cleanup: no destroy method found')
            }
          } catch (e) {
            console.warn('Error destroying previous Tappay instance:', e)
          }
        }

        // 初始化 Tappay Direct
        window.TPDirect.setupSDK(
          TAPPAY_CONFIG.APP_ID,
          TAPPAY_CONFIG.APP_KEY,
          TAPPAY_CONFIG.ENV
        )

        // 確保 DOM 元素存在
        const cardContainer = document.getElementById('card-container')

        if (!cardContainer) {
          console.error('Required DOM elements not found')
          this.error = '頁面元素載入失敗，請重新整理頁面'
          this.sdkLoading = false
          return
        }

        // 等待一下確保 DOM 完全準備好
        await new Promise(resolve => setTimeout(resolve, 100))

        // 使用正確的 API 方式
        try {
          // 根據 API 簽名，第一個參數應該是 DOM 元素或選擇器
          window.TPDirect.card.setup('#card-container', {
            lineHeight: '24px'
          })
          
          // setup 方法可能不返回實例，直接使用 window.TPDirect.card
          this.tappay = window.TPDirect.card
          
          console.log('Setup result:', this.tappay)
        } catch (setupError) {
          console.error('Tappay setup error:', setupError)
          this.error = '付款系統初始化失敗，請重新整理頁面'
          this.sdkLoading = false
          return
        }

        // 設置成功
        this.sdkLoading = false
        console.log('Tappay initialized successfully')
        
      } catch (error) {
        console.error('Tappay initialization error:', error)
        this.error = '付款系統初始化失敗，請重新整理頁面'
        this.sdkLoading = false
      }
    },

    async handlePaymentSuccess(result) {
      try {
        console.log('Full Tappay result:', result)
        console.log('Result status:', result.status)
        console.log('Result prime:', result.prime)
        console.log('Result type:', typeof result.prime)
        
        if (!result.prime) {
          throw new Error('Tappay 沒有返回 prime，請檢查卡片資料')
        }
        
        const requestData = {
          tappay: {
            order_id: this.orderId,
            prime: result.prime,
            payment_method: 'card'
          }
        }
        
        console.log('Sending payment data:', requestData)
        console.log('Order ID:', this.orderId)
        console.log('Prime:', result.prime)
        
        // 確保數據格式正確
        const payload = JSON.stringify(requestData)
        console.log('JSON payload:', payload)
        
        // 使用 Tappay 的 prime 進行付款
        const response = await http.post('/api/v1/tappay/process-payment', requestData)
        
        const data = await response.json()
        
        if (data.success) {
          console.log('Payment processed successfully:', data)
          
          if (data.requires_3ds && data.payment_url) {
            // 需要 3D Secure 驗證，重定向到驗證頁面
            console.log('Redirecting to 3D Secure verification:', data.payment_url)
            window.location.href = data.payment_url
          } else {
            // 直接付款成功，跳轉到付款成功頁面
            this.$router.push(`/checkout/done?order_id=${this.orderId}&payment_method=card`)
          }
        } else {
          throw new Error(data.error || '付款處理失敗')
        }
      } catch (error) {
        console.error('Error handling payment success:', error)
        this.error = error.message || '付款成功但處理訂單時發生錯誤'
        this.loading = false
      }
    },

    goBack() {
      this.$router.push('/checkout/order')
    }
  },
  metaInfo() {
    return {
      title: '付款頁面 - 完成您的訂單',
      meta: [
        { name: 'description', content: '完成您的付款以確認您的訂單。安全便捷的線上付款體驗。' },
        { name: 'keywords', content: '付款,線上付款,信用卡,Apple Pay,LINE Pay,結帳' },
        { property: 'og:title', content: '付款頁面 - 完成您的訂單' },
        { property: 'og:description', content: '完成您的付款以確認您的訂單。安全便捷的線上付款體驗。' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: window.location.href },
        { name: 'twitter:title', content: '付款頁面 - 完成您的訂單' },
        { name: 'twitter:description', content: '完成您的付款以確認您的訂單。安全便捷的線上付款體驗。' }
      ]
    }
  },
  created() {
    // 檢查是否為訪客結帳
    this.isGuestCheckout = this.$route.query.guest === 'true'
    
    // 如果是訪客結帳，使用傳遞的訂單ID
    if (this.isGuestCheckout) {
      this.orderId = parseInt(this.$route.query.order_id)
      console.log('訪客結帳模式，訂單ID:', this.orderId)
    } else {
      // 會員結帳：檢查用戶是否已登入
      if (!this.$store.getters.isAuthenticated) {
        this.$router.push('/login')
        return
      }
    }
    
    // 從 store 或 localStorage 獲取訂單資訊
    this.orderInfo = {
      shippingMethod: this.$store.state.shippingMethod || 'family-mart',
      address: this.$store.state.orderInfo?.address || '台北市中山區中山路一段1號',
      firstName: this.$store.state.orderInfo?.firstName || '小明',
      lastName: this.$store.state.orderInfo?.lastName || '陳',
      phone: this.$store.state.orderInfo?.phone || '0912345678',
      email: this.$store.state.orderInfo?.email || 'test@test.com'
    }
  },
  
  async mounted() {
    // 載入可用的付款方式
    await this.loadAvailablePaymentMethods()
    
    // 如果是访客结账，载入订单详情
    if (this.isGuestCheckout) {
      await this.loadGuestOrderDetails()
    }
    
    // 不再自動初始化 Tappay SDK，等用戶選擇付款方式時再初始化
  },
  
  beforeDestroy() {
    // 清理 Tappay 實例
    if (this.tappay) {
      try {
        // 檢查是否有 destroy 方法
        if (typeof this.tappay.destroy === 'function') {
          this.tappay.destroy()
        } else if (typeof this.tappay.remove === 'function') {
          this.tappay.remove()
        } else {
          console.log('Tappay instance cleanup: no destroy method found')
        }
      } catch (e) {
        console.warn('Error destroying Tappay instance:', e)
      }
    }
  }
}
</script>

<style scoped>
.tappay-payment-page {
  padding: 2rem 0;
  margin-top: 80px; /* 添加 margin-top 避免被導航欄蓋住 */
}

.checkout-progress {
  margin-bottom: 2rem;
}

.custom-progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.step-number.active {
  background-color: #007bff;
  color: white;
}

.step-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.progress-line {
  width: 60px;
  height: 2px;
  background-color: #e9ecef;
}

.order-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.order-summary h3 {
  margin-bottom: 1rem;
  color: #333;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

.summary-item.total {
  font-weight: bold;
  font-size: 1.125rem;
  border-top: 1px solid #dee2e6;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

/* 付款方式選擇 */
.payment-method-selection {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.payment-method-selection h3 {
  margin-bottom: 1.5rem;
  color: #333;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.payment-option {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.payment-option:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.payment-icons {
  display: flex;
  gap: 0.5rem;
}

.payment-icons img {
  height: 24px;
  width: auto;
}

.option-icon {
  height: 32px;
  width: auto;
}

.option-name {
  font-weight: bold;
  font-size: 1.125rem;
  color: #333;
}

.option-desc {
  color: #6c757d;
  font-size: 0.875rem;
}

/* 付款卡片 */
.payment-card {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.payment-form h3 {
  margin-bottom: 1.5rem;
  color: #333;
}

.payment-info {
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.payment-info p {
  margin: 0;
  color: #0056b3;
  font-size: 0.875rem;
}

.payment-info p + p {
  margin-top: 0.5rem;
}

.credit-card-form {
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #333;
}

.card-input-field {
  width: 100%;
  height: 48px;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem;
  font-size: 1rem;
  transition: all 0.2s;
  position: relative;
}

.card-input-field:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  outline: none;
}

.card-input-field:hover {
  border-color: #adb5bd;
}

/* 載入狀態 */
.card-input-field.loading {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.card-input-field.loading::after {
  content: '載入中...';
  position: absolute;
  top: 50%;
  left: 1rem;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 0.875rem;
}

/* Tappay SDK 樣式覆蓋 */
.card-input-field input {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
  font-size: 16px !important;
  font-family: inherit !important;
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* 確保 Tappay 輸入欄位有正確的樣式 */
.card-input-field .tappay-field {
  width: 100% !important;
  height: 100% !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  font-size: 16px !important;
  font-family: inherit !important;
}

.payment-btn {
  width: 100%;
  padding: 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.125rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
}

.payment-btn:hover:not(:disabled) {
  background-color: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.payment-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.payment-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 1rem;
  margin: 1rem 0;
  color: #721c24;
  font-size: 0.875rem;
}

.supported-cards {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #dee2e6;
}

.supported-cards h4 {
  margin-bottom: 1rem;
  color: #333;
}

.card-icons {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.card-icons img {
  height: 32px;
  width: auto;
}

.security-notice {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #dee2e6;
}

.security-notice h4 {
  margin-bottom: 1rem;
  color: #333;
}

.security-notice ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.security-notice li {
  padding: 0.25rem 0;
  color: #6c757d;
  font-size: 0.875rem;
  position: relative;
  padding-left: 1.5rem;
}

.security-notice li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #28a745;
  font-weight: bold;
}

.back-button {
  text-align: center;
  margin-top: 2rem;
}

.back-button .btn {
  padding: 0.75rem 2rem;
  font-size: 1rem;
}

/* Apple Pay 和 Line Pay 按鈕樣式 */
.apple-pay-container,
.line-pay-container {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
}

.apple-pay-button,
.line-pay-button {
  width: 100%;
  max-width: 300px;
  height: 48px;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .tappay-payment-page {
    padding: 1rem 0;
  }
  
  .payment-card {
    padding: 1.5rem;
  }
  
  .payment-method-selection {
    padding: 1.5rem;
  }
  
  .custom-progress-bar {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .progress-line {
    width: 2px;
    height: 20px;
  }
}
</style> 
